import Image from "next/image";
import Link from "next/link";
import RightArrowIcon from "../icons/RightArrowIcon";

const AiSupportSection = () => {
  const cards = [
    {
      title: "Identity Stamp",
      description:
        "Build a digital vehicle identity by recording the physical VIN, license plate, make, and model in seconds. GPS and timestamp data create a verified record of the exact asset present at handoff, helping prevent plate swaps, registration mix-ups, and vehicle identity fraud before the keys change hands.",
      position: "lg:left-2 lg:top-8 xl:left-6 xl:top-10",
    },
    {
      title: "Usage & System Status",
      description:
        "Record the vehicle’s exact usage and system status at the moment of the stamp. Capture odometer mileage, and active dashboard warning lights to establish a documented baseline for identifying excess mileage, fuel discrepancies, and newly appearing vehicle warnings.",
      position: "lg:right-2 lg:top-8 xl:right-6 xl:top-10",
    },
    {
      title: "Exterior Stamp",
      description:
        "Create a complete digital record of the vehicle’s exterior condition with 360° high-resolution photos of every body panel, wheel, and trim detail. Each image is tied to its capture time and location, creating a clear visual baseline that helps identify new damage, prevent false claims, and protect your deductible.",
      position: "lg:left-2 lg:bottom-6 xl:left-6 xl:bottom-12",
    },
    {
      title: "Interior Stamp",
      description:
        "Document the vehicle’s interior condition before and after the drive, capturing damage, stains, deployed airbags, and other visible abnormalities. Time and location records establish when and where each condition was documented, providing clear evidence to determine responsibility and support recovery claims.",
      position: "lg:right-2 lg:bottom-6 xl:right-6 xl:bottom-12",
    },
  ];

  return (
    <div className="bg-[#0A2540] min-h-200 flex w-full items-center justify-center">
      <div className="max-w-360 w-full mx-auto px-4">
        <h2
          className="text-center font-montserrat font-bold max-w-4xl mx-auto w-full py-10
               text-[26px] sm:text-[34px] md:text-[42px] lg:text-[48px]
               leading-[1.2] tracking-tight text-white"
        >
          Don’t Just Document It.{" "}
          <span className="bg-gradient-to-r from-[#5B9CFF] to-[#A5D0FF] bg-clip-text text-transparent">
            Stamp It.
          </span>
          <br />
          <span
            className="block mt-3 text-[18px] sm:text-[22px] md:text-[26px] lg:text-[28px] 
                   font-medium text-white/75 tracking-normal"
          >
            Let AI Turn Every Capture Into Proof.
          </span>
        </h2>

        {/* ==================== MOBILE / TABLET LAYOUT ==================== */}
        <section className="py-15 w-full flex flex-col lg:hidden justify-center items-center gap-10">
          {/* Cards – one after another */}
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
            {cards.map((card, index) => (
              <div key={index} data-aos="zoom-in" className="h-full">
                <div
                  className="
                    w-full h-full
                    rounded-md
                    border border-white/15
                    bg-white/4
                    backdrop-blur-2xl
                    shadow-[0_0_40px_rgba(45,101,242,0.15)]
                    p-5
                    overflow-hidden
                    relative
                    before:absolute before:inset-0
                    before:rounded-md
                    before:bg-[radial-gradient(circle_at_top_left,rgba(45,101,242,0.18),transparent_55%)]
                  "
                >
                  <div className="relative z-10">
                    <h3 className="text-[22px] font-semibold text-white mb-3">
                      {card.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-6">
                      {card.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Animation */}
          <div className="relative w-full flex justify-center">
            <Image
              src="/images/aiSupportImg.webp"
              className="relative z-20 w-full max-w-120 h-auto"
              alt="ai support img"
              width={540}
              height={300}
              quality={100}
            />
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-[1.7] opacity-50 rounded-full bg-[#2D65F2] blur-[90px] z-10"
            />
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
              <div className="relative w-full max-w-105 aspect-[1.5] bg-[#0A2540]">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-contain mix-blend-plus-lighter"
                >
                  <source src="/videos/ai_support.mov" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* ==================== DESKTOP LAYOUT (lg+) ==================== */}
        <section className="relative py-15 w-full min-h-170 lg:min-h-180 hidden lg:block">
          {/* Centered + smaller animation */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-95 xl:max-w-105 z-10">
            <Image
              src="/images/aiSupportImg.png"
              className="relative z-20 w-full h-auto"
              alt="ai support img"
              width={700}
              height={300}
              quality={100}
            />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-[1.7] opacity-50 rounded-full bg-[#2D65F2] blur-[90px] z-10" />
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
              <div className="relative w-full aspect-[1.5] bg-[#0A2540]">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-contain mix-blend-plus-lighter"
                >
                  <source src="/videos/ai_support.mov" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>

          {/* 4 Cards positioned around the cars */}
          {cards.map((card, index) => (
            <div
              key={index}
              data-aos="zoom-in"
              className={`absolute w-70 lg:w-100 ${card.position}`}
            >
              <div
                className="
                  w-full h-full
                  rounded-md
                  border border-white/15
                  bg-white/4
                  backdrop-blur-2xl
                  shadow-[0_0_40px_rgba(45,101,242,0.15)]
                  p-5
                  overflow-hidden
                  relative
                  before:absolute before:inset-0
                  before:rounded-md
                  before:bg-[radial-gradient(circle_at_top_left,rgba(45,101,242,0.18),transparent_55%)]
                "
              >
                <div className="relative z-10">
                  <h3 className="text-[22px] xl:text-[24px] font-semibold text-white mb-3">
                    {card.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-6">
                    {card.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
};

export default AiSupportSection;
